import { IconButton, InputBase, Paper } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { SendIcon } from "../../../shared/icons/sendIcon";

interface ChatInputProps {
  placeholder?: string;
  onSend: (text: string) => void;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  placeholder = "Escribe tu mensaje...",
  onSend,
  disabled,
}) => {
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Mantener el foco en el input
  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [disabled]);

  // Mantener el foco cuando el componente se monta
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current && !disabled) {
        inputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleSend = () => {
    if (value.trim()) {
      onSend(value.trim());
      setValue("");
      // Mantener el foco después de enviar
      setTimeout(() => {
        if (inputRef.current && !disabled) {
          inputRef.current.focus();
        }
      }, 0);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "start",
        borderRadius: "28px",
        boxShadow: "0 0 0 1px #1976d222",
        background: "#fff",
        px: 2,
        py: 0.5,
        minHeight: 120,
        border: "1px solid #f3e7e0",
        width: "100%",
        paddingTop: 2,
        paddingLeft: 4,
        position: "relative",
      }}
    >
      <InputBase
        ref={inputRef}
        multiline
        maxRows={5}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        sx={{
          flex: 1,
          fontSize: 16,
          color: "#222",
          background: "transparent",
          border: "none",
          outline: "none",
          resize: "none",
          p: 0,
          width: "100%", // Asegurar que ocupe todo el ancho disponible
          minHeight: 90, // Altura mínima para el área de texto
          "& textarea": {
            maxHeight: 90,
            overflowY: "auto",
            scrollbarWidth: "thin",
            width: "100%",
            minHeight: 90,
            padding: 0,
            margin: 0,
            border: "none",
            outline: "none",
            resize: "none",
          },
          "& .MuiInputBase-input": {
            width: "100%",
            minHeight: 90,
            padding: 0,
            margin: 0,
          },
        }}
        inputProps={{
          style: {
            maxHeight: 90,
            overflowY: "auto",
            width: "100%",
            minHeight: 90,
            padding: 0,
            margin: 0,
          },
        }}
      />
      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={!value.trim() || disabled}
        sx={{
          position: "absolute",
          right: 8,
          top: "75%",
          transform: "translateY(-50%)",
          color: "#1976d2",
          background: "transparent",
          opacity: !value.trim() || disabled ? 0.5 : 1,
          zIndex: 1, // Asegurar que esté por encima del input
        }}
      >
        <SendIcon
          fill={!value.trim() || disabled ? "#d5d5d5" : "#1976d2"}
          stroke={!value.trim() || disabled ? "#ffffff" : "#1976d2"}
        />
      </IconButton>
    </Paper>
  );
};

export default ChatInput;
